import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, FileDown, Plus, Pencil, Trash2, Check, X, Star, MailCheck, CalendarX } from "lucide-react";
import {
  adminLogin, fetchAdminData, API,
  adminListBlogPosts, adminCreateBlogPost, adminUpdateBlogPost, adminDeleteBlogPost,
  adminApproveReview, adminDeleteReview,
  fetchBlockedDates, adminAddBlockedDate, adminDeleteBlockedDate,
} from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import OrnamentalDivider from "@/components/OrnamentalDivider";
import axios from "axios";

export default function Admin() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState({ bookings: [], enquiries: [], contact: [], vouchers: [], schoolBookings: [], corpBookings: [], reviews: [], yyBookings: [] });
  const [posts, setPosts] = useState([]);

  const loadPosts = async () => {
    try {
      const p = await adminListBlogPosts(password);
      setPosts(p);
    } catch (err) {
      console.error("Failed to load blog posts:", err);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminLogin(password);
      const d = await fetchAdminData(password);
      setData(d);
      setAuthed(true);
      // Load blog after auth
      try {
        const p = await adminListBlogPosts(password);
        setPosts(p);
      } catch (err) {
        console.error("Failed to load blog posts on login:", err);
      }
    } catch (err) {
      toast.error("Wrong password");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    setAuthed(false);
    setPassword("");
    setData({ bookings: [], enquiries: [], contact: [], vouchers: [], schoolBookings: [], corpBookings: [], reviews: [], yyBookings: [] });
    setPosts([]);
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-6 py-24" data-testid="admin-login">
        <div className="stamp">Restricted</div>
        <h1 className="serif text-4xl text-[#6E2638] mt-4">Admin</h1>
        <OrnamentalDivider />
        <form onSubmit={login} className="card-warm rounded-2xl p-8">
          <Label>Password</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password" autoFocus required />
          <button className="btn-primary mt-6 w-full justify-center" disabled={busy} data-testid="admin-login-btn">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  const cell = "px-3 py-2 text-sm border-b border-[#E5D9C5]";

  const downloadCsv = async (kind) => {
    const res = await fetch(`${API}/export/${kind}.csv`, {
      headers: { "X-Admin-Password": password },
    });
    if (!res.ok) { toast.error("Export failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${kind}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="admin-dashboard">
      <div className="flex justify-between items-center">
        <div>
          <div className="stamp">Admin</div>
          <h1 className="serif text-4xl text-[#6E2638] mt-3">Bookings, Vouchers &amp; Journal</h1>
        </div>
        <button onClick={logout} className="btn-outline-gold" data-testid="admin-logout">
          <LogOut size={16} /> Sign out
        </button>
      </div>
      <OrnamentalDivider />

      <Tabs defaultValue="bookings">
        <TabsList className="bg-[#F5EFEB] flex-wrap h-auto">
          <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings ({data.bookings.length})</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries ({data.enquiries.length})</TabsTrigger>
          <TabsTrigger value="contact" data-testid="tab-contact">Contact ({data.contact.length})</TabsTrigger>
          <TabsTrigger value="vouchers" data-testid="tab-vouchers">Vouchers ({data.vouchers.length})</TabsTrigger>
          <TabsTrigger value="schools" data-testid="tab-schools">Schools ({data.schoolBookings.length})</TabsTrigger>
          <TabsTrigger value="corporate" data-testid="tab-corporate">Corporate ({data.corpBookings.length})</TabsTrigger>
          <TabsTrigger value="yesteryear" data-testid="tab-yesteryear">Yesteryear ({data.yyBookings.length})</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews ({data.reviews.length})</TabsTrigger>
          <TabsTrigger value="dates" data-testid="tab-dates">Dates</TabsTrigger>
          <TabsTrigger value="blog" data-testid="tab-journal">Journal ({posts.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings">
          <div className="flex justify-end mt-4">
            <button onClick={() => downloadCsv("bookings")} className="btn-outline-gold" data-testid="export-bookings">
              <FileDown size={16} /> Export CSV
            </button>
          </div>
          <div className="overflow-x-auto card-warm rounded-2xl p-4 mt-4">
            <table className="w-full" data-testid="bookings-table">
              <thead className="text-left text-xs uppercase tracking-widest text-[#1E4637]">
                <tr><th className={cell}>Date</th><th className={cell}>Slot</th><th className={cell}>Name</th><th className={cell}>Email</th><th className={cell}>Guests</th><th className={cell}>Total</th><th className={cell}>Voucher</th><th className={cell}>Review email</th></tr>
              </thead>
              <tbody>
                {data.bookings.map((b) => (
                  <tr key={b.id}>
                    <td className={cell}>{b.tour_date}</td>
                    <td className={cell}>{b.time_slot}</td>
                    <td className={cell}>{b.full_name}</td>
                    <td className={cell}>{b.email}</td>
                    <td className={cell}>{b.guests}</td>
                    <td className={cell}>${b.total_price_aud?.toFixed(2)}</td>
                    <td className={cell + " font-mono text-xs"}>{b.voucher_code || "—"}</td>
                    <td className={cell}>
                      {b.review_email_sent ? (
                        <span className="inline-flex items-center gap-1 text-xs text-[#1E4637]"><MailCheck size={12} /> Sent</span>
                      ) : (
                        <button
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/reviews/admin/send-request/${b.id}`, {},
                                { headers: { "X-Admin-Password": password } });
                              toast.success("Review request sent");
                              const d = await fetchAdminData(password);
                              setData(d);
                            } catch (_) { toast.error("Send failed"); }
                          }}
                          className="text-xs text-[#6E2638] underline"
                          data-testid={`send-review-req-${b.id}`}
                        >
                          Send now
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {data.bookings.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-[#1A1817]/60">No bookings yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="enquiries">
          <div className="flex justify-end mt-4">
            <button onClick={() => downloadCsv("enquiries")} className="btn-outline-gold" data-testid="export-enquiries">
              <FileDown size={16} /> Export CSV
            </button>
          </div>
          <div className="overflow-x-auto card-warm rounded-2xl p-4 mt-4">
            <table className="w-full" data-testid="enquiries-table">
              <thead className="text-left text-xs uppercase tracking-widest text-[#1E4637]">
                <tr><th className={cell}>Type</th><th className={cell}>Contact</th><th className={cell}>Organisation</th><th className={cell}>Email</th><th className={cell}>Group</th><th className={cell}>Preferred</th></tr>
              </thead>
              <tbody>
                {data.enquiries.map((e) => (
                  <tr key={e.id}>
                    <td className={cell + " capitalize"}>{e.tour_type}</td>
                    <td className={cell}>{e.contact_name}</td>
                    <td className={cell}>{e.organisation}</td>
                    <td className={cell}>{e.email}</td>
                    <td className={cell}>{e.group_size}</td>
                    <td className={cell}>{e.preferred_date || "—"}</td>
                  </tr>
                ))}
                {data.enquiries.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-[#1A1817]/60">No enquiries yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="contact">
          <div className="flex justify-end mt-4">
            <button onClick={() => downloadCsv("contact")} className="btn-outline-gold" data-testid="export-contact">
              <FileDown size={16} /> Export CSV
            </button>
          </div>
          <div className="overflow-x-auto card-warm rounded-2xl p-4 mt-4">
            <table className="w-full" data-testid="contact-table">
              <thead className="text-left text-xs uppercase tracking-widest text-[#1E4637]">
                <tr><th className={cell}>Name</th><th className={cell}>Email</th><th className={cell}>Subject</th><th className={cell}>Message</th></tr>
              </thead>
              <tbody>
                {data.contact.map((c) => (
                  <tr key={c.id}>
                    <td className={cell}>{c.name}</td>
                    <td className={cell}>{c.email}</td>
                    <td className={cell}>{c.subject || "—"}</td>
                    <td className={cell + " max-w-md"}>{c.message}</td>
                  </tr>
                ))}
                {data.contact.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-[#1A1817]/60">No messages yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="vouchers">
          <div className="overflow-x-auto card-warm rounded-2xl p-4 mt-4">
            <table className="w-full" data-testid="vouchers-table">
              <thead className="text-left text-xs uppercase tracking-widest text-[#1E4637]">
                <tr><th className={cell}>Date</th><th className={cell}>Code</th><th className={cell}>Buyer</th><th className={cell}>Recipient</th><th className={cell}>Seats</th><th className={cell}>Remaining</th><th className={cell}>Paid</th></tr>
              </thead>
              <tbody>
                {data.vouchers.map((v) => (
                  <tr key={v.id}>
                    <td className={cell}>{new Date(v.created_at).toLocaleDateString("en-AU")}</td>
                    <td className={cell + " font-mono text-xs"}>{v.code}</td>
                    <td className={cell}>{v.buyer_name}<br /><span className="text-xs text-[#1A1817]/60">{v.buyer_email}</span></td>
                    <td className={cell}>{v.recipient_name || "—"}<br /><span className="text-xs text-[#1A1817]/60">{v.recipient_email || ""}</span></td>
                    <td className={cell}>{v.seats}</td>
                    <td className={cell}>{v.seats_remaining}</td>
                    <td className={cell}>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${v.payment_status === "paid" ? "bg-[#1E4637] text-white" : "bg-[#E5D9C5] text-[#1A1817]"}`}>
                        {v.payment_status}
                      </span>
                    </td>
                  </tr>
                ))}
                {data.vouchers.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[#1A1817]/60">No vouchers yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="blog">
          <BlogAdmin password={password} posts={posts} onReload={loadPosts} />
        </TabsContent>

        <TabsContent value="dates">
          <BlockedDatesAdmin password={password} />
        </TabsContent>

        <TabsContent value="reviews">
          <ReviewsAdmin
            password={password}
            reviews={data.reviews}
            onReload={async () => {
              try {
                const d = await fetchAdminData(password);
                setData(d);
              } catch (err) {
                console.error("Failed to reload admin data:", err);
              }
            }}
          />
        </TabsContent>

        <TabsContent value="corporate">
          <div className="overflow-x-auto card-warm rounded-2xl p-4 mt-4">
            <table className="w-full" data-testid="corp-bookings-table">
              <thead className="text-left text-xs uppercase tracking-widest text-[#1E4637]">
                <tr>
                  <th className={cell}>Date</th>
                  <th className={cell}>Company</th>
                  <th className={cell}>Program</th>
                  <th className={cell}>Tier</th>
                  <th className={cell}>Contact</th>
                  <th className={cell}>Preferred</th>
                  <th className={cell}>Attendees</th>
                  <th className={cell}>Paid</th>
                  <th className={cell}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.corpBookings.map((c) => (
                  <tr key={c.id}>
                    <td className={cell}>{new Date(c.created_at).toLocaleDateString("en-AU")}</td>
                    <td className={cell + " serif text-[#6E2638]"}>{c.company_name}</td>
                    <td className={cell}>{c.package_title}</td>
                    <td className={cell + " text-xs"}>{c.tier_label || "—"}</td>
                    <td className={cell}>{c.contact_name}<br /><span className="text-xs text-[#1A1817]/60">{c.contact_email}</span></td>
                    <td className={cell}>{c.preferred_date || "—"}</td>
                    <td className={cell}>{c.attendee_count || "—"}</td>
                    <td className={cell}>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${c.payment_status === "paid" ? "bg-[#1E4637] text-white" : "bg-[#E5D9C5] text-[#1A1817]"}`}>
                        {c.payment_status}
                      </span>
                    </td>
                    <td className={cell}>${c.total_price_aud?.toFixed(2)}{c.discount_aud ? <><br /><span className="text-xs text-[#1E4637]">15% off −${c.discount_aud.toFixed(2)}</span></> : null}</td>
                  </tr>
                ))}
                {data.corpBookings.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-[#1A1817]/60">No corporate bookings yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="yesteryear">
          <div className="overflow-x-auto card-warm rounded-2xl p-4 mt-4">
            <table className="w-full" data-testid="yy-bookings-table">
              <thead className="text-left text-xs uppercase tracking-widest text-[#1E4637]">
                <tr>
                  <th className={cell}>Requested</th>
                  <th className={cell}>Venue</th>
                  <th className={cell}>Session</th>
                  <th className={cell}>Contact</th>
                  <th className={cell}>Preferred</th>
                  <th className={cell}>Audience</th>
                  <th className={cell}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {data.yyBookings.map((y) => (
                  <tr key={y.id}>
                    <td className={cell}>{new Date(y.created_at).toLocaleDateString("en-AU")}</td>
                    <td className={cell + " serif text-[#6E2638]"}>{y.venue_name}</td>
                    <td className={cell}>{y.option_label}</td>
                    <td className={cell}>{y.contact_name}<br /><span className="text-xs text-[#1A1817]/60">{y.contact_email} {y.contact_phone ? `· ${y.contact_phone}` : ""}</span></td>
                    <td className={cell}>{y.preferred_date || "—"}</td>
                    <td className={cell}>{y.audience_size || "—"}</td>
                    <td className={cell + " text-xs"}>{y.notes || "—"}</td>
                  </tr>
                ))}
                {data.yyBookings.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[#1A1817]/60">No Yesteryear requests yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="schools">
          <div className="overflow-x-auto card-warm rounded-2xl p-4 mt-4">
            <table className="w-full" data-testid="school-bookings-table">
              <thead className="text-left text-xs uppercase tracking-widest text-[#1E4637]">
                <tr>
                  <th className={cell}>Date</th>
                  <th className={cell}>School</th>
                  <th className={cell}>Program</th>
                  <th className={cell}>Contact</th>
                  <th className={cell}>Preferred</th>
                  <th className={cell}>Students</th>
                  <th className={cell}>Paid</th>
                  <th className={cell}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.schoolBookings.map((s) => (
                  <tr key={s.id}>
                    <td className={cell}>{new Date(s.created_at).toLocaleDateString("en-AU")}</td>
                    <td className={cell + " serif text-[#6E2638]"}>{s.school_name}</td>
                    <td className={cell}>{s.year_level}<br /><span className="text-xs text-[#1A1817]/60">{s.package_title}</span></td>
                    <td className={cell}>{s.contact_name}<br /><span className="text-xs text-[#1A1817]/60">{s.contact_email}</span></td>
                    <td className={cell}>{s.preferred_date || "—"}</td>
                    <td className={cell}>{s.student_count || "—"}</td>
                    <td className={cell}>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${s.payment_status === "paid" ? "bg-[#1E4637] text-white" : "bg-[#E5D9C5] text-[#1A1817]"}`}>
                        {s.payment_status}
                      </span>
                    </td>
                    <td className={cell}>${s.total_price_aud?.toFixed(2)}</td>
                  </tr>
                ))}
                {data.schoolBookings.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-[#1A1817]/60">No school bookings yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BlockedDatesAdmin({ password }) {
  const [dates, setDates] = useState([]);
  const [newDate, setNewDate] = useState("");
  const [newReason, setNewReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const docs = await fetchBlockedDates();
      setDates(docs || []);
    } catch (err) {
      console.error("Failed to load blocked dates:", err);
    }
  };
  useState(() => { load(); }, []);
  // effect on mount
  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e.preventDefault();
    if (!newDate) { toast.error("Pick a date"); return; }
    setBusy(true);
    try {
      await adminAddBlockedDate(password, newDate, newReason.trim() || null);
      toast.success(`${newDate} blocked`);
      setNewDate(""); setNewReason("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add");
    } finally { setBusy(false); }
  };

  const remove = async (date) => {
    if (!window.confirm(`Unblock ${date}? Bookings will re-open on this date.`)) return;
    try {
      await adminDeleteBlockedDate(password, date);
      toast.success(`${date} unblocked`);
      await load();
    } catch (err) {
      toast.error("Failed to unblock");
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="mt-4 space-y-4" data-testid="dates-admin">
      <form onSubmit={add} className="card-warm rounded-2xl p-6">
        <div className="serif text-xl text-[#6E2638] flex items-center gap-2">
          <CalendarX size={20} /> Block a date
        </div>
        <p className="text-xs text-[#1A1817]/70 mt-1">
          The individual booking calendar will grey out any date you add here.
        </p>
        <div className="grid md:grid-cols-3 gap-3 mt-4">
          <div>
            <Label htmlFor="new-block-date">Date *</Label>
            <Input id="new-block-date" type="date" value={newDate} min={today}
              onChange={(e) => setNewDate(e.target.value)}
              data-testid="input-block-date" required />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="new-block-reason">Reason (optional)</Label>
            <Input id="new-block-reason" value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              placeholder="e.g. Private group booking, family, unavailable…"
              maxLength={120}
              data-testid="input-block-reason" />
          </div>
        </div>
        <button type="submit" disabled={busy || !newDate} className="btn-primary mt-5" data-testid="add-block-date">
          {busy ? <><Loader2 size={14} className="animate-spin" /> Adding…</> :
            <><Plus size={14} /> Block this date</>}
        </button>
      </form>

      <div className="card-warm rounded-2xl p-4 overflow-x-auto">
        <table className="w-full" data-testid="blocked-dates-table">
          <thead className="text-left text-xs uppercase tracking-widest text-[#1E4637]">
            <tr>
              <th className="px-3 py-2 text-sm border-b border-[#E5D9C5]">Date</th>
              <th className="px-3 py-2 text-sm border-b border-[#E5D9C5]">Day</th>
              <th className="px-3 py-2 text-sm border-b border-[#E5D9C5]">Reason</th>
              <th className="px-3 py-2 text-sm border-b border-[#E5D9C5]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {dates.map((d) => {
              const day = new Date(d.date + "T00:00:00").toLocaleDateString("en-AU", { weekday: "long" });
              const past = d.date < today;
              return (
                <tr key={d.date} className={past ? "opacity-50" : ""}>
                  <td className="px-3 py-2 text-sm border-b border-[#E5D9C5] serif text-[#6E2638] font-mono">{d.date}</td>
                  <td className="px-3 py-2 text-sm border-b border-[#E5D9C5]">{day}{past && " · past"}</td>
                  <td className="px-3 py-2 text-sm border-b border-[#E5D9C5]">{d.reason || <span className="text-[#1A1817]/50 italic">—</span>}</td>
                  <td className="px-3 py-2 text-sm border-b border-[#E5D9C5]">
                    <button onClick={() => remove(d.date)}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-semibold text-[#6E2638] hover:bg-[#6E2638]/10"
                      data-testid={`unblock-${d.date}`}>
                      <Trash2 size={12} /> Unblock
                    </button>
                  </td>
                </tr>
              );
            })}
            {dates.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-[#1A1817]/60">No blocked dates yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReviewsAdmin({ password, reviews, onReload }) {
  const [busy, setBusy] = useState(null);

  const toggleApprove = async (id, approved) => {
    setBusy(id);
    try {
      await adminApproveReview(password, id, !approved);
      toast.success(!approved ? "Review published" : "Review hidden");
      await onReload();
    } catch (_) { toast.error("Update failed"); }
    finally { setBusy(null); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    setBusy(id);
    try {
      await adminDeleteReview(password, id);
      toast.success("Review deleted");
      await onReload();
    } catch (_) { toast.error("Delete failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="mt-4 space-y-4" data-testid="reviews-admin">
      <div className="rounded-xl bg-[#F5EFEB] border border-[#E5D9C5] p-4 text-sm text-[#1A1817]/85">
        Reviews are sent to your email as they arrive. Approve to publish to the home page — you never edit the content, only choose to display or hide.
      </div>

      {reviews.length === 0 && (
        <div className="text-center py-16 card-warm rounded-2xl text-[#1A1817]/60" data-testid="reviews-empty">
          No reviews yet.
        </div>
      )}

      {reviews.map((r) => (
        <div key={r.id} className="card-warm rounded-2xl p-6" data-testid={`review-row-${r.id}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <div className="serif text-lg text-[#6E2638]">{r.author_name}</div>
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <Star key={i} size={12} className="text-[#D4AF37]" fill="#D4AF37" />
                  ))}
                </div>
                <span className={`text-[10px] uppercase tracking-[0.24em] px-2 py-0.5 rounded-full ${r.approved ? "bg-[#1E4637] text-white" : "bg-[#E5D9C5] text-[#1A1817]"}`}>
                  {r.approved ? "Published" : "Pending"}
                </span>
              </div>
              <blockquote className="serif italic text-[#1A1817] text-base mt-2 leading-relaxed">
                &ldquo;{r.quote}&rdquo;
              </blockquote>
              <div className="text-xs text-[#1A1817]/60 mt-3">
                Submitted {new Date(r.created_at).toLocaleDateString("en-AU")}
                {r.tour_date && <> · Tour on {r.tour_date}</>}
                {r.author_email && <> · <a href={`mailto:${r.author_email}`} className="text-[#6E2638] underline">{r.author_email}</a></>}
              </div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button
                onClick={() => toggleApprove(r.id, r.approved)}
                disabled={busy === r.id}
                data-testid={`toggle-review-${r.id}`}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold ${r.approved ? "bg-[#E5D9C5] text-[#1A1817] hover:bg-[#D4AF37]/40" : "bg-[#1E4637] text-white hover:bg-[#1E4637]/90"}`}
              >
                {busy === r.id ? <Loader2 size={12} className="animate-spin" /> :
                  r.approved ? <><X size={12} /> Hide</> : <><Check size={12} /> Publish</>}
              </button>
              <button
                onClick={() => del(r.id)}
                disabled={busy === r.id}
                data-testid={`delete-review-${r.id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#6E2638] hover:bg-[#6E2638]/10"
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function BlogAdmin({ password, posts, onReload }) {
  const [editing, setEditing] = useState(null); // null | { id?, title, ... }
  const [saving, setSaving] = useState(false);

  const startNew = () => setEditing({
    title: "", excerpt: "", body: "", cover_image_url: "", published: true,
  });
  const startEdit = (p) => setEditing({ ...p });
  const cancel = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    if (!editing.title.trim() || !editing.body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await adminUpdateBlogPost(password, editing.id, editing);
        toast.success("Post updated");
      } else {
        await adminCreateBlogPost(password, editing);
        toast.success("Post published");
      }
      setEditing(null);
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await adminDeleteBlogPost(password, id);
      toast.success("Post deleted");
      onReload();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  if (editing) {
    return (
      <form onSubmit={save} className="card-warm rounded-2xl p-6 mt-4" data-testid="blog-editor">
        <div className="flex items-center justify-between mb-4">
          <h3 className="serif text-2xl text-[#6E2638]">{editing.id ? "Edit story" : "New story"}</h3>
          <button type="button" onClick={cancel} className="text-sm text-[#1A1817]/60 hover:text-[#6E2638]">
            Cancel
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="post-title">Title</Label>
            <Input id="post-title" value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              data-testid="post-title" required />
          </div>
          <div>
            <Label htmlFor="post-excerpt">Excerpt (short summary)</Label>
            <Textarea id="post-excerpt" rows={2} value={editing.excerpt || ""}
              onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
              data-testid="post-excerpt" />
          </div>
          <div>
            <Label htmlFor="post-cover">Cover image URL (optional)</Label>
            <Input id="post-cover" value={editing.cover_image_url || ""}
              onChange={(e) => setEditing({ ...editing, cover_image_url: e.target.value })}
              data-testid="post-cover" placeholder="https://..." />
          </div>
          <div>
            <Label htmlFor="post-body">Body</Label>
            <Textarea id="post-body" rows={16} value={editing.body}
              onChange={(e) => setEditing({ ...editing, body: e.target.value })}
              data-testid="post-body" required className="font-serif" />
            <p className="text-xs text-[#1A1817]/60 mt-1">
              Line breaks preserved. Write in plain prose — no need for HTML.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={editing.published}
              onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
              data-testid="post-published" />
            Published (visible on public site)
          </label>
        </div>

        <button type="submit" disabled={saving} className="btn-primary mt-6" data-testid="save-post">
          {saving ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : (editing.id ? "Update story" : "Publish story")}
        </button>
      </form>
    );
  }

  return (
    <div className="mt-4">
      <div className="flex justify-end">
        <button onClick={startNew} className="btn-primary" data-testid="new-post-btn">
          <Plus size={16} /> New story
        </button>
      </div>
      <div className="card-warm rounded-2xl p-4 mt-4 overflow-x-auto">
        <table className="w-full" data-testid="posts-table">
          <thead className="text-left text-xs uppercase tracking-widest text-[#1E4637]">
            <tr>
              <th className="px-3 py-2 text-sm border-b border-[#E5D9C5]">Title</th>
              <th className="px-3 py-2 text-sm border-b border-[#E5D9C5]">Slug</th>
              <th className="px-3 py-2 text-sm border-b border-[#E5D9C5]">Status</th>
              <th className="px-3 py-2 text-sm border-b border-[#E5D9C5]">Created</th>
              <th className="px-3 py-2 text-sm border-b border-[#E5D9C5]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id}>
                <td className="px-3 py-2 text-sm border-b border-[#E5D9C5] serif text-[#6E2638]">{p.title}</td>
                <td className="px-3 py-2 text-sm border-b border-[#E5D9C5] font-mono text-xs">{p.slug}</td>
                <td className="px-3 py-2 text-sm border-b border-[#E5D9C5]">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${p.published ? "bg-[#1E4637] text-white" : "bg-[#E5D9C5] text-[#1A1817]"}`}>
                    {p.published ? "Live" : "Draft"}
                  </span>
                </td>
                <td className="px-3 py-2 text-sm border-b border-[#E5D9C5]">{new Date(p.created_at).toLocaleDateString("en-AU")}</td>
                <td className="px-3 py-2 text-sm border-b border-[#E5D9C5]">
                  <div className="flex gap-2">
                    <button onClick={() => startEdit(p)} className="p-1.5 rounded hover:bg-[#F5EFEB] text-[#6E2638]" data-testid={`edit-post-${p.slug}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => del(p.id)} className="p-1.5 rounded hover:bg-[#F5EFEB] text-[#6E2638]" data-testid={`delete-post-${p.slug}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {posts.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-[#1A1817]/60">No stories yet. Write your first one.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
